import { Crop, Expand, ImagePlus, RotateCcw, RotateCw, Trash2 } from "lucide-react";
import type { ChangeEvent } from "react";
import { useId, useRef } from "react";
import type { PreparedImage } from "../lib/types";

interface ImageDropzoneProps {
  title: string;
  image?: PreparedImage;
  error?: string;
  busy?: boolean;
  onSelect: (file: File) => void;
  onPreview: () => void;
  onCrop: () => void;
  onRotate: (direction: -1 | 1) => void;
  onClear: () => void;
}

export function ImageDropzone({
  title,
  image,
  error,
  busy = false,
  onSelect,
  onPreview,
  onCrop,
  onRotate,
  onClear
}: ImageDropzoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) onSelect(file);
    event.target.value = "";
  }

  return (
    <section className="upload-panel" aria-busy={busy}>
      <div className="panel-heading">
        <h2>{title}</h2>
        {image ? (
          <div className="icon-row">
            <button
              className="icon-button"
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              aria-label={`更换${title}`}
              title="更换"
            >
              <ImagePlus size={18} />
            </button>
            <button
              className="icon-button"
              type="button"
              onClick={onPreview}
              disabled={busy}
              aria-label={`放大查看${title}`}
              title="放大"
            >
              <Expand size={18} />
            </button>
            <button
              className="icon-button"
              type="button"
              onClick={onCrop}
              disabled={busy}
              aria-label={`裁剪${title}`}
              title="裁剪"
            >
              <Crop size={18} />
            </button>
            <button
              className="icon-button"
              type="button"
              onClick={() => onRotate(-1)}
              disabled={busy}
              aria-label={`${title}逆时针旋转`}
              title="逆时针旋转"
            >
              <RotateCcw size={18} />
            </button>
            <button
              className="icon-button"
              type="button"
              onClick={() => onRotate(1)}
              disabled={busy}
              aria-label={`${title}顺时针旋转`}
              title="顺时针旋转"
            >
              <RotateCw size={18} />
            </button>
            <button
              className="icon-button danger"
              type="button"
              onClick={onClear}
              disabled={busy}
              aria-label={`清除${title}`}
              title="清除"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ) : null}
      </div>

      <button
        className={`dropzone ${image ? "has-image" : ""}`}
        type="button"
        onClick={() => (image ? onPreview() : inputRef.current?.click())}
        disabled={busy}
        aria-label={image ? `放大查看${title}` : `选择${title}`}
      >
        {image ? (
          <>
            <img src={image.dataUri} alt={title} />
            <span className="zoom-hint" aria-hidden="true">
              <Expand size={18} />
            </span>
          </>
        ) : (
          <span className="empty-upload">
            <ImagePlus size={28} />
            <span>选择图片</span>
          </span>
        )}
        {busy ? <span className="processing-shade">处理中</span> : null}
      </button>

      <input
        id={inputId}
        ref={inputRef}
        className="sr-only"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleChange}
      />

      <div className="image-meta" aria-live="polite">
        {image ? (
          <span>
            {image.width} x {image.height} · {(image.bytes / 1024 / 1024).toFixed(2)} MB
          </span>
        ) : (
          <span>JPG / PNG / WebP</span>
        )}
      </div>
      {error ? <p className="field-error">{error}</p> : null}
    </section>
  );
}
